import { Timestamp } from 'firebase/firestore';

/**
 * The progress rules every screen of a course must agree on: chapter list, quiz entry, Next button,
 * progress bar and the course/category completion that unlocks sign-off. They used to be re-derived
 * in each component from different fields (`lockedChapter`, `isVideoWatched`, `completedAt`), so a
 * chapter could show a checkmark and still refuse to open the next one.
 *
 * The single source of truth is `completedAt`: a chapter is done when it has one, and nothing else
 * (in particular the stored `lockedChapter` flag) decides what the learner may open.
 */
export interface ChapterProgress {
	id?: string;
	hasQuiz?: boolean;
	hasVideo?: boolean;
	isVideoWatched?: boolean;
	completedAt?: any;
	approvedQuiz?: boolean;
	quizScore?: number | null;
	quizAnswers?: any[] | null;
	quizAttempt?: number;
	lockedChapter?: boolean;
	videoCurrentTime?: number | null;
}

export function isChapterComplete(chapter: ChapterProgress | null | undefined): boolean {
	return !!chapter?.completedAt;
}

// Completed chapters over the chapters the learner is actually enrolled in. Matches the server's
// recalculation, which counts `chapter_enrollments` rather than the `total_chapters` stored at
// enrollment time (that one drifts when chapters are removed and pinned courses below 100%).
export function computeCompletedPercentage(chapters: ChapterProgress[] | null | undefined): number {
	const list = chapters ?? [];
	if (!list.length) {
		return 0;
	}
	const completed = list.filter(isChapterComplete).length;
	return Math.min(100, Math.round((completed / list.length) * 100));
}

// Guided courses gate a chapter on the previous one being finished; nothing else locks a chapter.
export function isChapterLocked(chapters: ChapterProgress[] | null | undefined, index: number, guidedMode: boolean): boolean {
	return !!guidedMode && index > 0 && !isChapterComplete(chapters?.[index - 1]);
}

// A chapter's quiz may be sat once its video is watched (or it has no video), and never while the
// chapter itself is locked.
export function isQuizAvailable(chapters: ChapterProgress[] | null | undefined, index: number, guidedMode: boolean): boolean {
	const chapter = chapters?.[index];
	if (!chapter?.hasQuiz || isChapterLocked(chapters, index, guidedMode)) {
		return false;
	}
	return !chapter.hasVideo || !!chapter.isVideoWatched || isChapterComplete(chapter);
}

// Share of a video that must have been watched for a playback failure to still count as finishing it.
export const END_FALLBACK_MIN_WATCHED = 0.9;

// Measured against the duration the player reports for the media actually loaded, never the one
// stored with the chapter, which does not always match the file.
export function isWatchedEnoughToComplete(watchedSeconds: number, mediaDuration: number): boolean {
	if (!isFinite(mediaDuration) || mediaDuration <= 0 || !isFinite(watchedSeconds)) {
		return false;
	}
	return watchedSeconds / mediaDuration >= END_FALLBACK_MIN_WATCHED;
}

// The first unfinished chapter after `fromIndex`, wrapping around to earlier gaps; null when every
// chapter is complete.
export function nextIncompleteChapterIndex(chapters: ChapterProgress[] | null | undefined, fromIndex: number): number | null {
	const list = chapters ?? [];
	const ahead = list.findIndex((chapter, i) => i > fromIndex && !isChapterComplete(chapter));
	if (ahead !== -1) {
		return ahead;
	}
	const anywhere = list.findIndex(chapter => !isChapterComplete(chapter));
	return anywhere === -1 ? null : anywhere;
}

/**
 * Forward progress for one chapter that the learner made on this device, kept until the server is
 * known to hold it. Every field only ever moves forward.
 */
export interface ProgressSignal {
	chapterId: string;
	quizAttempt: number;
	videoWatched?: boolean;
	completedAtMillis?: number | null;
	approvedQuiz?: boolean;
	quizScore?: number | null;
	quizAnswers?: any[] | null;
	videoPosition?: number | null;
}

const attemptOf = (chapter: any): number => Number(chapter?.quizAttempt ?? chapter?.quiz_attempt ?? 0) || 0;
const completedAtOf = (chapter: any): any => chapter?.completedAt ?? chapter?.completed_at ?? null;
const videoWatchedOf = (chapter: any): boolean => !!(chapter?.isVideoWatched ?? chapter?.is_video_watched);
const quizScoreOf = (chapter: any): number | null => chapter?.quizScore ?? chapter?.quiz_score ?? null;
const approvedOf = (chapter: any): boolean => (chapter?.approvedQuiz ?? chapter?.approved_quiz) === true;
const positionOf = (chapter: any): number => Number(chapter?.videoCurrentTime ?? chapter?.video_current_time ?? 0) || 0;

const toMillis = (value: any): number | null => {
	if (!value) {
		return null;
	}
	if (typeof value.toMillis === 'function') {
		return value.toMillis();
	}
	if (typeof value.seconds === 'number') {
		return value.seconds * 1000 + Math.floor((value.nanoseconds ?? 0) / 1e6);
	}
	const parsed = value instanceof Date ? value.getTime() : Date.parse(value);
	return Number.isFinite(parsed) ? parsed : null;
};

// The progress a chapter carries, as a signal; null when there is nothing worth keeping.
export function signalFromChapter(chapter: ChapterProgress): ProgressSignal | null {
	if (!chapter?.id) {
		return null;
	}
	const signal: ProgressSignal = { chapterId: chapter.id, quizAttempt: attemptOf(chapter) };
	let hasProgress = false;
	if (videoWatchedOf(chapter)) {
		signal.videoWatched = true;
		hasProgress = true;
	}
	const completedAt = toMillis(completedAtOf(chapter));
	if (completedAt) {
		signal.completedAtMillis = completedAt;
		hasProgress = true;
	}
	const quizScore = quizScoreOf(chapter);
	if (quizScore !== null && quizScore !== undefined) {
		signal.quizScore = quizScore;
		signal.approvedQuiz = approvedOf(chapter);
		signal.quizAnswers = chapter.quizAnswers ?? null;
		hasProgress = true;
	}
	const position = positionOf(chapter);
	if (position > 0 && !signal.videoWatched) {
		signal.videoPosition = position;
		hasProgress = true;
	}
	return hasProgress ? signal : null;
}

// Combines two signals for the same chapter, keeping the furthest progress of each.
export function mergeSignals(stored: ProgressSignal | undefined, incoming: ProgressSignal): ProgressSignal {
	if (!stored || incoming.quizAttempt > stored.quizAttempt) {
		// A newer attempt makes the older attempt's quiz state irrelevant; video progress carries over.
		return {
			...incoming,
			videoWatched: incoming.videoWatched || stored?.videoWatched || undefined,
			videoPosition: Math.max(incoming.videoPosition ?? 0, stored?.videoPosition ?? 0) || undefined,
		};
	}
	if (incoming.quizAttempt < stored.quizAttempt) {
		return {
			...stored,
			videoWatched: stored.videoWatched || incoming.videoWatched || undefined,
			videoPosition: Math.max(incoming.videoPosition ?? 0, stored.videoPosition ?? 0) || undefined,
		};
	}
	const hasScore = (s: ProgressSignal) => s.quizScore !== null && s.quizScore !== undefined;
	const quizSource = hasScore(stored) ? stored : incoming;
	return {
		chapterId: stored.chapterId,
		quizAttempt: stored.quizAttempt,
		videoWatched: stored.videoWatched || incoming.videoWatched || undefined,
		completedAtMillis: stored.completedAtMillis ?? incoming.completedAtMillis ?? undefined,
		quizScore: quizSource.quizScore,
		approvedQuiz: stored.approvedQuiz || incoming.approvedQuiz || undefined,
		quizAnswers: quizSource.quizAnswers,
		videoPosition: Math.max(incoming.videoPosition ?? 0, stored.videoPosition ?? 0) || undefined,
	};
}

// Whether a stored chapter (camelCase model or snake_case document) already holds everything the
// signal carries. Only then may the local copy be dropped.
export function isSignalSatisfied(chapter: any, signal: ProgressSignal): boolean {
	if (!chapter) {
		return false;
	}
	if (attemptOf(chapter) > signal.quizAttempt) {
		// The server moved on to a later attempt (a granted retake): this signal is obsolete.
		return true;
	}
	if (signal.videoWatched && !videoWatchedOf(chapter)) {
		return false;
	}
	if (attemptOf(chapter) < signal.quizAttempt) {
		return !signal.completedAtMillis && (signal.quizScore === null || signal.quizScore === undefined);
	}
	if (signal.completedAtMillis && !completedAtOf(chapter)) {
		return false;
	}
	if (signal.quizScore !== null && signal.quizScore !== undefined && quizScoreOf(chapter) === null) {
		return false;
	}
	if (signal.approvedQuiz && !approvedOf(chapter)) {
		return false;
	}
	if (signal.videoPosition && !videoWatchedOf(chapter) && positionOf(chapter) < signal.videoPosition) {
		return false;
	}
	return true;
}

/**
 * Re-applies locally kept progress to chapters read from the server. Only ever moves a chapter
 * forward, and never pastes one attempt's quiz result onto another. Returns whether anything changed.
 */
export function applyProgressSignals(chapters: ChapterProgress[] | null | undefined, signals: ProgressSignal[]): boolean {
	if (!Array.isArray(chapters) || !signals?.length) {
		return false;
	}
	const byId = new Map(signals.map(signal => [signal.chapterId, signal]));
	let changed = false;
	chapters.forEach((chapter, index) => {
		const signal = chapter?.id ? byId.get(chapter.id) : undefined;
		if (!signal) {
			return;
		}
		if (signal.videoWatched && !chapter.isVideoWatched) {
			chapter.isVideoWatched = true;
			changed = true;
		}
		if (signal.videoPosition && !chapter.isVideoWatched && (chapter.videoCurrentTime ?? 0) < signal.videoPosition) {
			chapter.videoCurrentTime = signal.videoPosition;
			changed = true;
		}
		if (attemptOf(chapter) !== signal.quizAttempt) {
			return;
		}
		if (signal.quizScore !== null && signal.quizScore !== undefined && (chapter.quizScore === null || chapter.quizScore === undefined)) {
			chapter.quizScore = signal.quizScore;
			chapter.quizAnswers = signal.quizAnswers ?? chapter.quizAnswers ?? [];
			changed = true;
		}
		if (signal.approvedQuiz && !chapter.approvedQuiz) {
			chapter.approvedQuiz = true;
			changed = true;
		}
		if (signal.completedAtMillis && !chapter.completedAt) {
			chapter.completedAt = Timestamp.fromMillis(signal.completedAtMillis);
			chapter.lockedChapter = false;
			const next = chapters[index + 1];
			if (next) {
				next.lockedChapter = false;
			}
			changed = true;
		}
	});
	return changed;
}

/**
 * Matches each chapter enrollment to its authored content.
 *
 * By id first. Two content problems break that and used to leave every chapter without a video or
 * questions: a translation re-created with new chapter ids (same chapters, different ids), and a
 * translation with no chapters at all. The first is recovered by position when the chapter counts
 * agree and no id matches; the second by looking the ids up in the course's other language versions.
 */
export function alignChapterContent<T extends { id?: string; isDeleted?: boolean }>(
	enrollmentIds: (string | null | undefined)[],
	referenced: T[] | null | undefined,
	siblings: T[][] = [],
): { chapters: (T | null)[]; strategy: 'id' | 'position' | 'sibling' | 'partial'; missing: number } {
	const ownChapters = referenced ?? [];
	const byId = new Map(ownChapters.filter(chapter => chapter?.id).map(chapter => [chapter.id, chapter]));
	const chapters: (T | null)[] = enrollmentIds.map(id => (id ? byId.get(id) ?? null : null));
	const unmatched = () => chapters.filter(chapter => !chapter).length;
	if (!unmatched()) {
		return { chapters, strategy: 'id', missing: 0 };
	}

	const active = ownChapters.filter(chapter => chapter && !chapter.isDeleted);
	if (chapters.every(chapter => !chapter) && active.length === enrollmentIds.length) {
		return { chapters: active.slice(), strategy: 'position', missing: 0 };
	}

	let usedSibling = false;
	enrollmentIds.forEach((id, index) => {
		if (chapters[index] || !id) {
			return;
		}
		for (const sibling of siblings) {
			const match = (sibling ?? []).find(chapter => chapter?.id === id);
			if (match) {
				chapters[index] = match;
				usedSibling = true;
				return;
			}
		}
	});
	const missing = unmatched();
	return { chapters, strategy: missing ? 'partial' : usedSibling ? 'sibling' : 'id', missing };
}
